import { z, ZodObject, ZodRawShape } from 'zod';
import { PAGE_PATHS } from '../types';

const globalSchema = z.object({
  state: z.string().optional(),
  code: z.string().optional(),
});

export const withDefaults = <T extends z.ZodTypeAny>(schema: T, ...defaults: Array<z.input<T>>): z.ZodEffects<T> =>
  schema.transform(input => ({
    ...defaults.reduce((acc, cur) => ({ ...acc, ...cur }), {}),
    ...input,
  }));

export const getMainSchema = <T extends Record<keyof typeof PAGE_PATHS, ZodObject<any>>>(schemas: T) =>
  Object.values(schemas)
    .reduce((acc, schema) => acc.merge(schema), globalSchema)
    .catchall(transformToStringArray);

export const mergeZodSchemas = (...schemas: ZodObject<Record<string, any>>[]): ZodObject<Record<string, any>> =>
  schemas.reduce((acc, schema) => acc.merge(schema), globalSchema).catchall(transformToStringArray);

export const transformToString = z.preprocess(
  arg => (arg === undefined ? undefined : Array.isArray(arg) ? z.coerce.string().parse(arg[0]) : z.coerce.string().parse(arg)),
  z.string().optional()
);

export const transformToNumber = z.preprocess(
  arg =>
    arg === undefined ? undefined : Array.isArray(arg) ? Number(z.coerce.number().parse(arg[0])) : Number(z.coerce.number().parse(arg)),
  z.number().optional()
);

export const transformToBoolean = z.preprocess(
  arg => (arg === undefined ? undefined : Array.isArray(arg) ? z.coerce.boolean().parse(arg[0]) : z.coerce.boolean().parse(arg)),
  z.boolean().optional()
);

export const transformToObject = (object: ZodRawShape) =>
  z.preprocess(
    arg =>
      arg === undefined
        ? undefined
        : Array.isArray(arg)
          ? typeof arg[0] === 'object'
            ? arg[0]
            : JSON.parse(z.coerce.string().parse(arg[0]))
          : typeof arg === 'object'
            ? arg
            : JSON.parse(z.coerce.string().parse(arg)),
    z.object(object).optional()
  );

export const transformToStringArray = z.preprocess(
  arg =>
    arg === '' // empty params are parsed to '' in old url's, example: ... &asset.site=& ... => asset.site = ''
      ? []
      : arg === undefined
        ? undefined
        : Array.isArray(arg)
          ? arg.map(x => (typeof x === 'object' ? JSON.stringify(x) : z.coerce.string().parse(x)))
          : typeof arg === 'object'
            ? [JSON.stringify(arg)]
            : [z.coerce.string().parse(arg)],
  z.string().array().optional()
);

export const transformToDateArray = z.preprocess(
  arg => (arg === undefined ? undefined : Array.isArray(arg) ? arg.map(x => z.coerce.date().parse(x)) : [z.coerce.date().parse(arg)]),
  z.date().array().optional()
);

export const transformToStringArrayOrNull = z.preprocess(
  arg =>
    arg === '' // empty params are parsed to '' in old url's, example: ... &asset.site=& ... => asset.site = ''
      ? []
      : arg === undefined
        ? undefined
        : arg === null
          ? null
          : Array.isArray(arg)
            ? arg.map(x => (typeof x === 'object' ? JSON.stringify(x) : z.coerce.string().parse(x)))
            : typeof arg === 'object'
              ? [JSON.stringify(arg)]
              : [z.coerce.string().parse(arg)],
  z.string().array().optional()
);

export const transformToStringOrNull = z.preprocess(arg => {
  if (arg === null || arg === undefined) {
    return arg;
  }
  const value = Array.isArray(arg) ? arg[0] : arg;
  if (value === null || value === undefined) {
    return value;
  }
  return z.coerce.string().parse(value);
}, z.string().nullable().optional());

export const transformToExpressionsObject = z.preprocess(
  arg => {
    if (arg === undefined) {
      return undefined;
    }
    const value = Array.isArray(arg) ? arg[0] : arg;
    if (typeof value === 'object') {
      return value;
    }
    try {
      return JSON.parse(z.coerce.string().parse(value));
    } catch (e) {
      console.error('Failed to parse expressions object:', e);
      return {}; // Return an empty object or handle error as appropriate
    }
  },
  z
    .record(
      z.string(),
      z.object({
        name: z.string(),
        function: z.string(),
        bucket: z.string().optional(),
        alias: z.string(),
      })
    )
    .optional()
);
